import {
  type CreateMarkupProfileRequest,
  type MarkupAppliedTo,
  type MarkupCategory,
  type MarkupKind,
  type MarkupProfileDetail,
  type MarkupScope,
} from '@utazzo/shared';
import { UzButton, UzDrawer, UzInput, UzNumberInput, UzSelect, UzSwitch } from '@utazzo/ui';
import React, { useEffect, useState } from 'react';

import { useCreateMarkupProfile, useUpdateMarkupProfile } from '../use-markup-profiles';

import { CityPicker } from '@/components/pickers/CityPicker';

/** Category rows in display order. `appliedTo` is hidden for TBO/OVERALL rows. */
const ROWS: { key: MarkupCategory; label: string; appliedTo: boolean }[] = [
  { key: 'HOTEL', label: 'Hotel (in-house)', appliedTo: true },
  // Still read by itinerary TBO hotel components. The standalone hotel funnel
  // ignores it - its markup is fixed at 2% in code (`HOTEL_MARKUP_BPS`).
  { key: 'TBO_HOTEL', label: 'Hotel (online, packages only)', appliedTo: false },
  { key: 'ACTIVITY', label: 'Activity', appliedTo: true },
  { key: 'TRANSFER', label: 'Transfer', appliedTo: true },
  { key: 'VAS', label: 'VAS (visa / meal)', appliedTo: true },
  { key: 'TBO_FLIGHT', label: 'Flight', appliedTo: false },
  { key: 'OVERALL', label: 'Overall top-up', appliedTo: false },
];

type RuleState = { kind: MarkupKind; value: number; appliedTo: MarkupAppliedTo };

const emptyRules = (): Record<MarkupCategory, RuleState> =>
  Object.fromEntries(
    ROWS.map((r) => [r.key, { kind: 'PERCENT' as MarkupKind, value: 0, appliedTo: 'GLOBAL' }]),
  ) as Record<MarkupCategory, RuleState>;

const KIND_OPTS = [
  { value: 'PERCENT', label: '%' },
  { value: 'FLAT', label: 'Flat' },
];
const APPLIED_OPTS = [
  { value: 'GLOBAL', label: 'Global (this master)' },
  { value: 'FIELD', label: 'Field (catalog markup)' },
  { value: 'MIXED', label: 'Mixed (field else global)' },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  profile?: MarkupProfileDetail | null;
}

export const MarkupProfileFormDrawer = ({
  open,
  onOpenChange,
  mode,
  profile,
}: Props): React.JSX.Element => {
  const isCreate = mode === 'create';
  const create = useCreateMarkupProfile();
  const update = useUpdateMarkupProfile();
  const pending = create.isPending || update.isPending;

  const [scope, setScope] = useState<MarkupScope>('DESTINATION');
  const [cityId, setCityId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [rules, setRules] = useState<Record<MarkupCategory, RuleState>>(emptyRules());

  useEffect(() => {
    if (!open) return;
    if (!isCreate && profile) {
      setScope(profile.scope);
      setCityId(profile.cityId);
      setName(profile.name);
      setIsActive(profile.isActive);
      const next = emptyRules();
      for (const r of profile.rules) {
        next[r.category] = { kind: r.kind, value: r.value, appliedTo: r.appliedTo };
      }
      setRules(next);
    } else {
      setScope('DESTINATION');
      setCityId(null);
      setName('');
      setIsActive(true);
      setRules(emptyRules());
    }
  }, [open, isCreate, profile]);

  const setRule = (cat: MarkupCategory, patch: Partial<RuleState>): void =>
    setRules((prev) => ({ ...prev, [cat]: { ...prev[cat], ...patch } }));

  const submit = (): void => {
    const payload: CreateMarkupProfileRequest = {
      scope,
      cityId: scope === 'DESTINATION' ? cityId : null,
      name: name.trim() || (scope === 'GLOBAL' ? 'Global default' : 'Destination markup'),
      isActive,
      // Only persist rules with a non-zero value (a zeroed category = no markup).
      rules: ROWS.filter((r) => rules[r.key].value > 0).map((r) => ({
        category: r.key,
        kind: rules[r.key].kind,
        value: rules[r.key].value,
        appliedTo: r.appliedTo ? rules[r.key].appliedTo : 'GLOBAL',
      })),
    };
    const done = (): void => onOpenChange(false);
    if (isCreate) create.mutate(payload, { onSuccess: done });
    else if (profile) update.mutate({ id: profile.id, payload }, { onSuccess: done });
  };

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add markup profile' : `Edit ${profile?.name ?? 'profile'}`}
      description="Per-category markup applied during itinerary pricing. One Global default, plus one per destination city."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton
            onClick={submit}
            loading={pending}
            disabled={scope === 'DESTINATION' && !cityId}
          >
            {isCreate ? 'Create' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      <div className="space-y-5">
        <UzSelect
          label="Scope"
          value={scope}
          onValueChange={(v) => setScope(v as MarkupScope)}
          options={[
            { value: 'GLOBAL', label: 'Global default' },
            { value: 'DESTINATION', label: 'Destination (per city)' },
          ]}
        />
        {scope === 'DESTINATION' && (
          <CityPicker label="Destination city" value={cityId} onValueChange={setCityId} />
        )}
        <UzInput
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={scope === 'GLOBAL' ? 'Global default' : 'e.g. Dubai markup'}
        />

        <div className="rounded-lg border border-zinc-200">
          <div className="border-b border-zinc-100 px-4 py-2.5 text-xs font-semibold tracking-wider text-zinc-500 uppercase">
            Markup by category
          </div>
          <div className="divide-y divide-zinc-100">
            {ROWS.map((row) => {
              const r = rules[row.key];
              return (
                <div key={row.key} className="px-4 py-3">
                  <div className="text-sm font-medium text-zinc-800">{row.label}</div>
                  <div className="mt-1.5 flex items-end gap-2">
                    <div className="w-24 flex-none">
                      <UzNumberInput
                        label="Value"
                        precision={2}
                        min={0}
                        value={r.value}
                        onValueChange={(v) => setRule(row.key, { value: Math.max(0, v ?? 0) })}
                      />
                    </div>
                    <div className="w-24 flex-none">
                      <UzSelect
                        label="Kind"
                        value={r.kind}
                        onValueChange={(v) => setRule(row.key, { kind: v as MarkupKind })}
                        options={KIND_OPTS}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      {row.appliedTo ? (
                        <UzSelect
                          label="Applied to"
                          value={r.appliedTo}
                          onValueChange={(v) =>
                            setRule(row.key, { appliedTo: v as MarkupAppliedTo })
                          }
                          options={APPLIED_OPTS}
                        />
                      ) : (
                        <div className="pb-2 text-[11px] text-zinc-400">n/a</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <UzSwitch
          label="Active"
          description="Inactive profiles are ignored by the quote engine."
          checked={isActive}
          onCheckedChange={setIsActive}
        />
      </div>
    </UzDrawer>
  );
};
